(() => {
  const money = (cents) => {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
        minimumFractionDigits: 2,
      }).format(value);
    } catch (_error) {
      return `$${value.toFixed(2)}`;
    }
  };

  class CurvaEasePairPrice extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.pairPrice = Number(this.dataset.pairPrice || 0);
      this.unitPrice = Number(this.dataset.unitPrice || 0);
      this.quantityInput = document.getElementById(`Quantity-${this.sectionId}`);
      this.variantPicker = document.querySelector(
        `variant-selects[data-section="${this.sectionId}"], variant-radios[data-section="${this.sectionId}"], hybrid-variant-picker[data-section="${this.sectionId}"]`
      );
      this.boundUpdate = () => requestAnimationFrame(() => this.update());
      this.quantityInput?.addEventListener('change', this.boundUpdate);
      this.variantPicker?.addEventListener('change', this.boundUpdate);
      this.stickyPrice = document.getElementById(`atc-price-${this.sectionId}`);
      if (this.stickyPrice && !this.stickyPrice.dataset.ceOriginalHtml) {
        this.stickyPrice.dataset.ceOriginalHtml = this.stickyPrice.innerHTML;
      }
      this.update();
    }

    disconnectedCallback() {
      this.quantityInput?.removeEventListener('change', this.boundUpdate);
      this.variantPicker?.removeEventListener('change', this.boundUpdate);
    }

    getCurrentUnitPrice() {
      const subtotalAmount = document.querySelector(`#subtotal-${this.sectionId} .subtotal-amount`);
      const refreshedPrice = Number(subtotalAmount?.getAttribute('price'));
      return Number.isFinite(refreshedPrice) && refreshedPrice > 0 ? refreshedPrice : this.unitPrice;
    }

    update() {
      const quantity = Math.max(1, Number(this.quantityInput?.value || 1));
      const unitPrice = this.getCurrentUnitPrice();
      const pairCount = Math.floor(quantity / 2);
      const regularTotal = unitPrice * quantity;
      const offerTotal = pairCount * this.pairPrice + (quantity % 2) * unitPrice;
      const savings = Math.max(0, regularTotal - offerTotal);

      this.dataset.quantity = String(quantity);
      this.querySelector('[data-ce-regular]')?.replaceChildren(document.createTextNode(money(regularTotal)));
      this.querySelector('[data-ce-final]')?.replaceChildren(document.createTextNode(money(offerTotal)));
      this.querySelector('[data-ce-save]')?.replaceChildren(document.createTextNode(`Save ${money(savings)}`));

      this.updateStickyPrice(quantity, regularTotal, offerTotal, savings);

      const message = this.querySelector('[data-ce-message]');
      if (!message) return;
      if (quantity === 1) {
        message.textContent = `Choose quantity 2 to get the pair for ${money(this.pairPrice)}.`;
      } else if (pairCount === 1 && quantity === 2) {
        message.textContent = 'Your pair offer will apply automatically in the cart and at checkout.';
      } else {
        message.textContent = `${pairCount} pair${pairCount === 1 ? '' : 's'} priced at the pair offer${quantity % 2 ? ', plus 1 regular-price item' : ''}.`;
      }
    }

    updateStickyPrice(quantity, regularTotal, offerTotal, savings) {
      if (!this.stickyPrice) return;
      if (quantity < 2) {
        const original = this.stickyPrice.dataset.ceOriginalHtml;
        if (original) this.stickyPrice.innerHTML = original;
        return;
      }
      this.stickyPrice.innerHTML = `
        <div class="ce-sticky-pair-price" aria-label="Pair offer total ${money(offerTotal)}">
          <s>${money(regularTotal)}</s>
          <strong>${money(offerTotal)}</strong>
          <small>Pair &amp; Save ${money(savings)}</small>
        </div>`;
    }
  }

  if (!customElements.get('curvaease-pair-price')) {
    customElements.define('curvaease-pair-price', CurvaEasePairPrice);
  }

  const cleanOpenCrotchLabels = (root = document) => {
    root.querySelectorAll('[data-ce-open-crotch-picker] legend span').forEach((label) => {
      label.textContent = label.textContent.replace(/-1PC$/i, '').trim();
    });
  };

  const ensureOpenCrotchSinglePiece = (root = document) => {
    root.querySelectorAll('[data-ce-open-crotch-picker]').forEach((picker) => {
      if (picker.dataset.ceInitialized === 'true') {
        cleanOpenCrotchLabels(picker);
        return;
      }

      const checkedColor = picker.querySelector('fieldset[data-option-index="1"] input:checked');
      const preferred = checkedColor && /-1PC$/i.test(checkedColor.value)
        ? checkedColor
        : picker.querySelector('fieldset[data-option-index="1"] input[value="Skin-1PC"]')
          || picker.querySelector('fieldset[data-option-index="1"] input[value$="-1PC"]');

      if (preferred) {
        picker.dataset.ceInitialized = 'true';
        preferred.checked = true;
        preferred.dispatchEvent(new Event('change', { bubbles: true }));
        window.setTimeout(() => cleanOpenCrotchLabels(picker), 0);
      } else {
        cleanOpenCrotchLabels(picker);
      }
    });
  };

  const activatePreview = (target) => {
    const slider = target.querySelector('product-card-slider') || target.closest('product-card-slider');
    (slider || target).classList.add('ce-hold-active');
    target.dataset.ceHeld = 'true';
  };

  const deactivatePreview = (target) => {
    const slider = target.querySelector('product-card-slider') || target.closest('product-card-slider');
    (slider || target).classList.remove('ce-hold-active');
    window.setTimeout(() => delete target.dataset.ceHeld, 260);
  };

  let holdTimer = null;
  let holdTarget = null;

  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse') return;
    const target = event.target.closest('.card__media, .ce-interactive-media');
    if (!target) return;
    holdTarget = target;
    holdTimer = window.setTimeout(() => activatePreview(target), 180);
  }, { passive: true });

  const endHold = () => {
    if (holdTimer) window.clearTimeout(holdTimer);
    holdTimer = null;
    if (holdTarget?.dataset.ceHeld === 'true') deactivatePreview(holdTarget);
    holdTarget = null;
  };

  document.addEventListener('pointerup', endHold, { passive: true });
  document.addEventListener('pointercancel', endHold, { passive: true });
  document.addEventListener('pointerleave', (event) => {
    if (event.pointerType !== 'mouse') endHold();
  }, { passive: true });

  document.addEventListener('click', (event) => {
    const target = event.target.closest('.card__media, .ce-interactive-media');
    if (target?.dataset.ceHeld === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      deactivatePreview(target);
    }
  }, true);

  document.addEventListener('focusin', (event) => {
    const media = event.target.closest('.card__media');
    const slider = media?.querySelector('product-card-slider');
    (slider || media)?.classList.add('ce-keyboard-preview');
  });

  document.addEventListener('focusout', (event) => {
    const media = event.target.closest('.card__media');
    const slider = media?.querySelector('product-card-slider');
    (slider || media)?.classList.remove('ce-keyboard-preview');
  });

  document.addEventListener('change', (event) => {
    if (event.target.closest('[data-ce-open-crotch-picker]')) {
      window.setTimeout(() => cleanOpenCrotchLabels(document), 0);
    }
  });

  document.addEventListener('DOMContentLoaded', () => ensureOpenCrotchSinglePiece(document));
  document.addEventListener('shopify:section:load', (event) => ensureOpenCrotchSinglePiece(event.target));

  class CurvaEasePairBuilder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ceReady === 'true') return;
      this.dataset.ceReady = 'true';
      this.pairPrice = Number(this.dataset.pairPrice || 0);
      this.isOpenCrotch = this.dataset.openCrotch === 'true';
      this.status = this.querySelector('[data-ce-pair-status]');
      this.button = this.querySelector('[data-ce-add-pair]');
      this.itemRoots = [...this.querySelectorAll('[data-ce-pair-item]')];

      try {
        this.product = JSON.parse(this.querySelector('[data-ce-product-json]')?.textContent || '{}');
      } catch (_error) {
        this.fail('The color picker could not load. Please use the regular Add to cart button.');
        return;
      }

      this.colorIndex = (this.product.options || []).findIndex((name) => String(name).toLowerCase().includes('color'));
      this.variants = (this.product.variants || []).filter((variant) => {
        if (!variant.available) return false;
        if ((variant.options || []).some((value) => /2\s*pcs|black-skin|brown-skin|black-brown/i.test(String(value)))) return false;
        if (!this.isOpenCrotch || this.colorIndex < 0) return true;
        return /-1pc$/i.test(String(variant.options?.[this.colorIndex] || ''));
      });

      if (!this.variants.length || this.itemRoots.length !== 2) {
        this.fail('This set is temporarily unavailable. Please use the regular Add to cart button.');
        return;
      }

      const first = this.variants[0];
      const firstColor = this.normalizedColor(first);
      const second = this.variants.find((variant) => this.normalizedColor(variant) !== firstColor) || first;
      this.setItemFromVariant(this.itemRoots[0], first);
      this.setItemFromVariant(this.itemRoots[1], second);

      this.itemRoots.forEach((item) => {
        item.addEventListener('change', () => {
          const variant = this.resolveVariant(item);
          if (variant) this.setItemFromVariant(item, variant);
          this.update();
        });
      });
      this.button?.addEventListener('click', () => this.addPair());
      this.update();
    }

    normalizedColor(variant) {
      if (this.colorIndex < 0) return '';
      return String(variant.options?.[this.colorIndex] || '').replace(/-1pc$/i, '').trim().toLowerCase();
    }

    selectedOptions(item) {
      const selected = new Map();
      item.querySelectorAll('[data-ce-option-index]').forEach((select) => {
        selected.set(Number(select.dataset.ceOptionIndex), select.value);
      });
      return selected;
    }

    resolveVariant(item) {
      const selected = this.selectedOptions(item);
      return this.variants.find((variant) => {
        return [...selected.entries()].every(([index, value]) => String(variant.options?.[index]) === String(value));
      }) || null;
    }

    setItemFromVariant(item, variant) {
      item.querySelectorAll('[data-ce-option-index]').forEach((select) => {
        const index = Number(select.dataset.ceOptionIndex);
        const value = String(variant.options?.[index] || '');
        if ([...select.options].some((option) => option.value === value)) select.value = value;
      });
      item.dataset.variantId = String(variant.id);
      item.dataset.variantPrice = String(variant.price);
      const status = item.querySelector('[data-ce-item-status]');
      if (status) {
        status.textContent = 'Available';
        delete status.dataset.unavailable;
      }
    }

    update() {
      const resolved = this.itemRoots.map((item) => this.resolveVariant(item));
      this.itemRoots.forEach((item, index) => {
        const status = item.querySelector('[data-ce-item-status]');
        if (!status) return;
        const available = Boolean(resolved[index]);
        status.textContent = available ? 'Available' : 'This combination is unavailable';
        if (available) delete status.dataset.unavailable;
        else status.dataset.unavailable = 'true';
      });

      const valid = resolved.every(Boolean);
      if (this.button) this.button.disabled = !valid;
      if (!valid) return;

      this.selectedVariants = resolved;
      const regular = resolved.reduce((sum, variant) => sum + Number(variant.price || 0), 0);
      const savings = Math.max(0, regular - this.pairPrice);
      this.querySelector('[data-ce-pair-regular]')?.replaceChildren(document.createTextNode(money(regular)));
      this.querySelector('[data-ce-pair-final]')?.replaceChildren(document.createTextNode(money(this.pairPrice)));
      this.querySelector('[data-ce-pair-savings]')?.replaceChildren(document.createTextNode(`Save ${money(savings)}`));
      if (this.button) this.button.textContent = `Add both to cart · ${money(this.pairPrice)}`;
      if (this.status) this.status.textContent = '';
    }

    async addPair() {
      if (!this.selectedVariants?.length || !this.button) return;
      const originalText = this.button.textContent;
      this.button.disabled = true;
      this.button.textContent = 'Adding both pieces…';
      if (this.status) this.status.textContent = '';

      const [first, second] = this.selectedVariants;
      const items = first.id === second.id
        ? [{ id: first.id, quantity: 2 }]
        : [{ id: first.id, quantity: 1 }, { id: second.id, quantity: 1 }];

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!response.ok) throw new Error('cart_add_failed');
        window.location.assign(`${root}cart`);
      } catch (_error) {
        this.button.disabled = false;
        this.button.textContent = originalText;
        if (this.status) this.status.textContent = 'We could not add the set. Please try again or use the regular Add to cart button.';
      }
    }

    fail(message) {
      if (this.status) this.status.textContent = message;
      if (this.button) this.button.disabled = true;
    }
  }

  if (!customElements.get('curvaease-pair-builder')) {
    customElements.define('curvaease-pair-builder', CurvaEasePairBuilder);
  }

  const CE_RELATED_RULES = {
    shape1: { products: [10649516867886, 10649703776558], codes: ['CE-XC-4F8M2Q7L9V5K', 'CE-XD-6T3N8W2R7J9P'] },
    shape2: { products: [10649516867886, 10649701646638], codes: ['CE-X-F1263DA3BBF96B', 'CE-X-FF14CE601BD39C'] },
    shape3: { products: [10649703776558, 10649701646638], codes: ['CE-X-2BCB0EBF1715FD', 'CE-X-6785348679AE6D'] },
    lift1: { products: [10649701843246, 10649701318958], codes: ['CE-X-6C32B7238A910F', 'CE-X-1A639DBD65E702'] },
    lift2: { products: [10649701843246, 10649701384494], codes: ['CE-X-8CCA04E5B5338A', 'CE-X-AA6DFCFDFF8D95'] },
    lift3: { products: [10649701843246, 10649516998958], codes: ['CE-X-8AA32425556FF5', 'CE-X-6CDEF2614B94AF'] },
    lift4: { products: [10649701318958, 10649701384494], codes: ['CE-X-5F0D89A72D7014', 'CE-X-F4EA7E46A8A063'] },
    lift5: { products: [10649701318958, 10649516998958], codes: ['CE-X-DC1C4EA7E24075', 'CE-X-2D5EC904C97D9A'] },
    lift6: { products: [10649701384494, 10649516998958], codes: ['CE-X-18F6C29CB273EC', 'CE-X-C83CF4EF41921D'] },
    sleep1: { products: [10649517130030, 10649701548334], codes: ['CE-X-D9C197CF20F8B2', 'CE-X-44F1C85F7EB03D'] },
    sleep2: { products: [10649517130030, 10649701613870], codes: ['CE-X-D38143EB3E080C', 'CE-X-407F50D47059F7'] },
    sleep3: { products: [10649701548334, 10649701613870], codes: ['CE-X-6626B221327897', 'CE-X-1D31EC5D65DF24'] },
  };
  const CE_RELATED_CODES = new Set(Object.values(CE_RELATED_RULES).flatMap((rule) => rule.codes));

  const optionNames = (product) => (product.options || []).map((option) => typeof option === 'string' ? option : option.name);
  const availableVariants = (product, singlePieceOnly = false) => {
    const names = optionNames(product);
    const colorIndex = names.findIndex((name) => String(name).toLowerCase().includes('color'));
    return (product.variants || []).filter((variant) => {
      if (!variant.available) return false;
      if ((variant.options || []).some((value) => /2\s*pcs|black-skin|brown-skin|black-brown/i.test(String(value)))) return false;
      if (!singlePieceOnly || colorIndex < 0) return true;
      return /-1pc$/i.test(String(variant.options?.[colorIndex] || ''));
    });
  };

  class CurvaEaseRelatedBuilder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ceReady === 'true') return;
      this.dataset.ceReady = 'true';
      try { this.currentProduct = JSON.parse(this.querySelector('[data-ce-related-current]')?.textContent || '{}'); }
      catch (_error) { return; }
      this.querySelectorAll('[data-ce-related-card]').forEach((card) => {
        const button = card.querySelector('[data-ce-open-related]');
        button?.addEventListener('click', () => this.toggleCard(card));
        card.querySelector('[data-ce-add-related]')?.addEventListener('click', () => this.addRelatedPair(card));
      });
    }

    toggleCard(card) {
      const panel = card.querySelector('[data-ce-related-panel]');
      const button = card.querySelector('[data-ce-open-related]');
      if (!panel || !button) return;
      const opening = panel.hidden;
      this.querySelectorAll('[data-ce-related-panel]').forEach((other) => { other.hidden = true; });
      this.querySelectorAll('[data-ce-open-related]').forEach((other) => other.setAttribute('aria-expanded', 'false'));
      panel.hidden = !opening;
      button.setAttribute('aria-expanded', String(opening));
      if (opening) {
        this.initializeCard(card);
        panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    initializeCard(card) {
      if (card.dataset.ceInitialized === 'true') return;
      card.dataset.ceInitialized = 'true';
      try { card.relatedProduct = JSON.parse(card.querySelector('[data-ce-related-product]')?.textContent || '{}'); }
      catch (_error) { this.fail(card, 'This related product could not load.'); return; }
      card.products = [this.currentProduct, card.relatedProduct];
      card.itemRoots = [...card.querySelectorAll('[data-ce-related-item]')];
      card.itemRoots.forEach((item, index) => {
        const product = card.products[index];
        const singlePiece = String(product.handle || '').includes('open-crotch');
        item.ceVariants = availableVariants(product, singlePiece);
        item.ceProduct = product;
        this.renderFields(item);
        item.addEventListener('change', () => this.updateCard(card));
      });
      this.updateCard(card);
    }

    renderFields(item) {
      const names = optionNames(item.ceProduct);
      const fields = item.querySelector('[data-ce-related-fields]');
      if (!fields || !item.ceVariants?.length) return;
      fields.replaceChildren();
      names.forEach((name, index) => {
        const values = [...new Set(item.ceVariants.map((variant) => String(variant.options?.[index] || '')))].filter(Boolean);
        const nameLower = String(name).toLowerCase();
        if (values.length === 1 && (nameLower.includes('ships from') || nameLower.includes('delivery'))) return;
        const label = document.createElement('label'); label.className = 'ce-related-builder__field';
        const caption = document.createElement('span'); caption.textContent = String(name).replace('Cup Size|Ships From', 'Style');
        const select = document.createElement('select'); select.dataset.ceRelatedOption = String(index); select.setAttribute('aria-label', String(name));
        values.forEach((value) => {
          const option = document.createElement('option'); option.value = value; option.textContent = value.replace(/-1pc$/i, '').replace(/（old）|\(old\)/i, '').trim(); select.append(option);
        });
        label.append(caption, select); fields.append(label);
      });
      this.setItemFromVariant(item, item.ceVariants[0]);
    }

    selectedOptions(item) {
      return new Map([...item.querySelectorAll('[data-ce-related-option]')].map((select) => [Number(select.dataset.ceRelatedOption), select.value]));
    }

    resolveVariant(item) {
      const selected = this.selectedOptions(item);
      return item.ceVariants.find((variant) => [...selected].every(([index, value]) => String(variant.options?.[index]) === String(value))) || null;
    }

    setItemFromVariant(item, variant) {
      item.querySelectorAll('[data-ce-related-option]').forEach((select) => {
        const value = String(variant.options?.[Number(select.dataset.ceRelatedOption)] || '');
        if ([...select.options].some((option) => option.value === value)) select.value = value;
      });
      item.dataset.variantId = String(variant.id); item.dataset.productId = String(item.ceProduct.id); item.dataset.variantPrice = String(variant.price);
    }

    updateCard(card) {
      const resolved = card.itemRoots.map((item) => {
        let variant = this.resolveVariant(item);
        if (!variant) {
          const selected = this.selectedOptions(item);
          const changed = [...selected.entries()].pop();
          variant = item.ceVariants.find((candidate) => !changed || String(candidate.options?.[changed[0]]) === String(changed[1])) || item.ceVariants[0];
          if (variant) this.setItemFromVariant(item, variant);
        }
        const status = item.querySelector('[data-ce-related-item-status]');
        if (status) { status.textContent = variant ? 'Available' : 'This combination is unavailable'; status.toggleAttribute('data-unavailable', !variant); }
        return variant;
      });
      const valid = resolved.every(Boolean);
      const add = card.querySelector('[data-ce-add-related]'); if (add) add.disabled = !valid;
      if (!valid) return;
      card.selectedVariants = resolved;
      const regular = resolved.reduce((sum, variant) => sum + Number(variant.price || 0), 0);
      card.querySelector('[data-ce-related-regular]')?.replaceChildren(document.createTextNode(money(regular)));
      card.querySelector('[data-ce-related-final]')?.replaceChildren(document.createTextNode(money(Math.max(0, regular - 800))));
      if (add) add.textContent = `Add both to cart · ${money(Math.max(0, regular - 800))}`;
      const status = card.querySelector('[data-ce-related-status]'); if (status) status.textContent = '';
    }

    async addRelatedPair(card) {
      if (!card.selectedVariants?.length) return;
      const button = card.querySelector('[data-ce-add-related]'); const status = card.querySelector('[data-ce-related-status]');
      const codes = String(card.dataset.pairCodes || '').split(',').filter(Boolean); const pairKey = card.dataset.pairKey;
      if (codes.length !== 2 || !CE_RELATED_RULES[pairKey]) { this.fail(card, 'This pair is temporarily unavailable.'); return; }
      button.disabled = true; button.textContent = 'Adding your pair…'; if (status) status.textContent = '';
      const root = window.Shopify?.routes?.root || '/';
      const items = card.selectedVariants.map((variant) => ({ id: variant.id, quantity: 1, properties: { _ce_related_pair: pairKey } }));
      try {
        const added = await fetch(`${root}cart/add.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
        if (!added.ok) throw new Error('cart_add_failed');
        const discounted = await fetch(`${root}cart/update.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ discount: codes.join(',') }) });
        if (!discounted.ok) throw new Error('discount_failed');
        const cart = await discounted.json();
        const applied = cart.items.flatMap((item) => item.line_level_discount_allocations || []).filter((allocation) => codes.includes(allocation.discount_application?.title));
        if (applied.length !== 2 || applied.reduce((sum, allocation) => sum + Number(allocation.amount || 0), 0) !== 800) throw new Error('discount_not_verified');
        window.setTimeout(() => window.location.assign(`${root}cart?ce_related_pair=${encodeURIComponent(pairKey)}`), 80);
      } catch (_error) {
        await fetch(`${root}cart/update.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ discount: '' }) }).catch(() => null);
        button.disabled = false; this.updateCard(card); if (status) status.textContent = 'The related-pair price could not be verified. Please try again.';
      }
    }

    fail(card, message) { const status = card.querySelector('[data-ce-related-status]'); if (status) status.textContent = message; const button = card.querySelector('[data-ce-add-related]'); if (button) button.disabled = true; }
  }

  if (!customElements.get('curvaease-related-builder')) customElements.define('curvaease-related-builder', CurvaEaseRelatedBuilder);

  const getActiveRelatedCodes = (cart) => [...new Set((cart.items || []).flatMap((item) => (item.line_level_discount_allocations || []).map((allocation) => allocation.discount_application?.title)).filter((title) => CE_RELATED_CODES.has(title)))];
  const relatedCartIsValid = (cart, codes) => {
    const entry = Object.entries(CE_RELATED_RULES).find(([, rule]) => rule.codes.some((code) => codes.includes(code)));
    if (!entry) return true;
    const [key, rule] = entry;
    if (!rule.codes.every((code) => codes.includes(code))) return false;
    return rule.products.every((productId) => {
      const lines = (cart.items || []).filter((item) => Number(item.product_id) === productId && item.properties?._ce_related_pair === key);
      return lines.reduce((sum, item) => sum + Number(item.quantity || 0), 0) === 1;
    });
  };
  let ceGuardPromise = null;
  const guardRelatedDiscounts = async () => {
    if (ceGuardPromise) return ceGuardPromise;
    ceGuardPromise = (async () => {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) return true;
      const cart = await response.json(); const codes = getActiveRelatedCodes(cart);
      if (!codes.length || relatedCartIsValid(cart, codes)) return true;
      await fetch(`${root}cart/update.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ discount: '' }) });
      return false;
    })().finally(() => { ceGuardPromise = null; });
    return ceGuardPromise;
  };
  document.addEventListener('DOMContentLoaded', () => { if (location.pathname.endsWith('/cart')) guardRelatedDiscounts(); });
  document.addEventListener('change', (event) => { if (event.target.closest('cart-items, cart-drawer, [data-cart-items]')) window.setTimeout(guardRelatedDiscounts, 900); });
  document.addEventListener('click', async (event) => {
    const checkout = event.target.closest('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]');
    if (!checkout || checkout.dataset.ceCheckoutVerified === 'true') return;
    event.preventDefault(); event.stopImmediatePropagation();
    await guardRelatedDiscounts();
    checkout.dataset.ceCheckoutVerified = 'true';
    if (checkout.tagName === 'A') window.location.assign(checkout.href); else checkout.click();
  }, true);

  const setupReferralForms = () => {
    document.querySelectorAll('[data-ce-referral-form]').forEach((form) => {
      if (form.dataset.ceReady === 'true') return;
      form.dataset.ceReady = 'true';
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const status = form.querySelector('[data-ce-referral-status]');
        const button = form.querySelector('button[type="submit"]');
        const friend = String(new FormData(form).get('friend_email') || '').trim().toLowerCase();
        const referrer = String(new FormData(form).get('referrer_email') || '').trim().toLowerCase();
        form.classList.remove('is-error', 'is-ready');
        if (!form.reportValidity()) return;
        if (friend === referrer) {
          form.classList.add('is-error');
          if (status) status.textContent = 'Please use two different email addresses. Self-referrals do not qualify.';
          return;
        }
        button.disabled = true;
        if (status) status.textContent = 'Preparing the $5 friend reward…';
        const root = window.Shopify?.routes?.root || '/';
        try {
          const response = await fetch(`${root}cart/update.js`, {
            method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: {
              'CurvaEase referral friend email': friend,
              'CurvaEase referrer email': referrer,
              'CurvaEase referral started': new Date().toISOString(),
              'CurvaEase referral terms': 'Accepted'
            } })
          });
          if (!response.ok) throw new Error('referral_not_saved');
          form.classList.add('is-ready');
          if (status) status.textContent = 'Your $5 friend reward is ready. Taking you to shop CurvaEase…';
          window.setTimeout(() => window.location.assign(`${root}discount/CURVAFRIEND5?redirect=${encodeURIComponent(`${root}collections?friend_reward=ready`)}`), 120);
        } catch (_error) {
          button.disabled = false;
          form.classList.add('is-error');
          if (status) status.textContent = 'We could not prepare the reward yet. Please try again.';
        }
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setupReferralForms); else setupReferralForms();
})();


/* CurvaEase scroll-triggered email welcome */
(() => {
  const STORAGE_KEY = 'curvaease_email_welcome_v1';
  const DISMISS_DAYS = 14;
  const readState = () => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch (_error) { return {}; }
  };
  const saveState = (value) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readState(), ...value })); } catch (_error) { /* storage can be blocked */ }
  };
  const setup = () => {
    const popup = document.querySelector('[data-ce-email-popup]');
    if (!popup || popup.dataset.ceReady === 'true') return;
    popup.dataset.ceReady = 'true';
    const dialog = popup.querySelector('.ce-email-popup__dialog');
    const form = popup.querySelector('#CeEmailPopupForm');
    const stateNode = document.querySelector('[data-ce-email-popup-state]');
    let serverState = {};
    try { serverState = JSON.parse(stateNode?.textContent || '{}'); } catch (_error) { serverState = {}; }
    let previousFocus = null;
    let closing = false;

    const open = () => {
      if (!popup.hidden || closing) return;
      previousFocus = document.activeElement;
      popup.hidden = false;
      popup.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('ce-email-popup-open');
      requestAnimationFrame(() => popup.classList.add('is-open'));
      window.setTimeout(() => (popup.querySelector('input[type="email"]') || dialog)?.focus(), 80);
    };
    const close = (remember = true) => {
      if (popup.hidden || closing) return;
      closing = true;
      popup.classList.add('is-closing');
      if (remember && !serverState.posted) saveState({ dismissedAt: Date.now() });
      window.setTimeout(() => {
        popup.hidden = true;
        popup.setAttribute('aria-hidden', 'true');
        popup.classList.remove('is-open', 'is-closing');
        document.documentElement.classList.remove('ce-email-popup-open');
        closing = false;
        previousFocus?.focus?.();
      }, 250);
    };
    popup.querySelectorAll('[data-ce-email-popup-close]').forEach((button) => button.addEventListener('click', () => close(true)));
    popup.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { close(true); return; }
      if (event.key !== 'Tab') return;
      const focusable = [...popup.querySelectorAll('button:not([disabled]),a[href],input:not([disabled])')].filter((node) => node.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });

    form?.addEventListener('submit', () => saveState({ submittedAt: Date.now() }));
    popup.querySelectorAll('[data-ce-copy-code]').forEach((button) => button.addEventListener('click', async () => {
      const code = button.dataset.ceCopyCode || '';
      try { await navigator.clipboard.writeText(code); } catch (_error) {
        const input = document.createElement('textarea'); input.value = code; input.style.position = 'fixed'; input.style.opacity = '0'; document.body.appendChild(input); input.select(); document.execCommand('copy'); input.remove();
      }
      const note = button.querySelector('small'); if (note) { note.textContent = 'Copied'; window.setTimeout(() => { note.textContent = 'Tap to copy'; }, 1800); }
    }));

    if (serverState.posted) {
      saveState({ subscribedAt: Date.now(), dismissedAt: 0 });
      fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: 'CURVAWELCOME1', attributes: { 'CurvaEase email welcome': 'Subscribed', 'CurvaEase welcome reward': 'CURVAWELCOME1' } })
      }).catch(() => null);
      open();
      return;
    }
    if (serverState.error) { open(); return; }
    if (/^\/(cart|checkouts?|account|challenge)(\/|$)/.test(location.pathname)) return;
    const stored = readState();
    if (stored.subscribedAt) return;
    if (stored.dismissedAt && Date.now() - stored.dismissedAt < DISMISS_DAYS * 86400000) return;
    let shown = false;
    const maybeOpen = () => {
      if (shown) return;
      const threshold = Math.min(360, Math.max(140, document.documentElement.scrollHeight * 0.08));
      if (window.scrollY < threshold) return;
      shown = true;
      window.removeEventListener('scroll', maybeOpen);
      open();
    };
    window.addEventListener('scroll', maybeOpen, { passive: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup); else setup();
})();
